import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RefreshCw } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { useI18n } from "../../i18n/I18nContext";
import { useSite } from "../../context/SiteContext";
import { api, formatApiError } from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";

const DEFAULT_DRAFT = {
    contact: { phones: [{}, {}, {}], emails: ["", ""], website: "", address: "" },
    hours: { mon_thu: "", fri: "", sat: "", sun: "" },
    socials: { facebook: "", instagram: "", whatsapp_default: "" },
    clients_satisfied: 500,
    identity: {},
    hero: {},
    services: [],
    about: {},
    cta_band: {},
};

const TABS = ["identity", "contact", "hours", "socials", "brand", "hero", "services", "about", "cta"];

export default function AdminSettings() {
    const { t, lang } = useI18n();
    const { site, setSite, refresh } = useSite();
    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    const [tab, setTab] = useState("contact");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (site) {
            const s = { ...DEFAULT_DRAFT, ...site };
            // Ensure arrays are properly sized
            s.contact = { ...DEFAULT_DRAFT.contact, ...(site.contact || {}) };
            while ((s.contact.phones || []).length < 3) s.contact.phones.push({ display: "", waNumber: "" });
            while ((s.contact.emails || []).length < 2) s.contact.emails.push("");
            s.hours = { ...DEFAULT_DRAFT.hours, ...(site.hours || {}) };
            s.socials = { ...DEFAULT_DRAFT.socials, ...(site.socials || {}) };
            s.identity = { ...(site.identity || {}) };
            s.hero = { ...(site.hero || {}) };
            s.services = (site.services || []).map((x) => ({ ...x }));
            s.about = { ...(site.about || {}) };
            s.about.values_fr = s.about.values_fr || [];
            s.about.values_en = s.about.values_en || [];
            s.cta_band = { ...(site.cta_band || {}) };
            setDraft(s);
        }
    }, [site]);

    const setPath = (path, value) => {
        setDraft((prev) => {
            const next = structuredClone(prev);
            let cur = next;
            const parts = path.split(".");
            for (let i = 0; i < parts.length - 1; i++) {
                const key = parts[i];
                cur[key] = cur[key] ?? {};
                cur = cur[key];
            }
            cur[parts[parts.length - 1]] = value;
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                contact: draft.contact,
                hours: draft.hours,
                socials: draft.socials,
                clients_satisfied: Number(draft.clients_satisfied) || 0,
                hero: draft.hero,
                services: draft.services,
                about: draft.about,
                cta_band: draft.cta_band,
                identity: draft.identity,
            };
            const { data } = await api.put("/admin/site", payload);
            setSite(data);
            await refresh();
            toast.success(t("settings.saved"));
        } catch (err) {
            toast.error(formatApiError(err) || t("settings.error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-10">
                <header className="flex flex-wrap items-end justify-between gap-6 mb-8">
                    <div>
                        <div className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-gold mb-2">
                            {t("admin.dashboard")}
                        </div>
                        <h1 className="font-display text-3xl text-brand-navy">{t("settings.title")}</h1>
                        <p className="text-gray-500 mt-1 text-sm">{t("settings.subtitle")}</p>
                    </div>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="settings-save-top"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Save className="w-4 h-4" /> {saving ? "…" : t("settings.save_all")}
                    </button>
                </header>

                {/* TABS */}
                <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-8" data-testid="settings-tabs">
                    {TABS.map((k) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setTab(k)}
                            data-testid={`settings-tab-${k}`}
                            className={`px-4 py-2.5 text-[0.7rem] tracking-[0.2em] uppercase font-semibold transition-colors border-b-2 -mb-px ${
                                tab === k
                                    ? "text-brand-navy border-brand-gold"
                                    : "text-gray-500 border-transparent hover:text-brand-navy"
                            }`}
                        >
                            {t(`settings.tabs.${k}`)}
                        </button>
                    ))}
                </div>

                <div className="bg-white border border-gray-100 p-6 lg:p-8" data-testid="settings-panel">
                    {tab === "identity" && (
                        <IdentityTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "contact" && (
                        <ContactTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "hours" && (
                        <HoursTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "socials" && (
                        <SocialsTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "brand" && (
                        <BrandTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "hero" && (
                        <HeroTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "services" && (
                        <ServicesTab draft={draft} setPath={setPath} setDraft={setDraft} t={t} />
                    )}
                    {tab === "about" && (
                        <AboutTab draft={draft} setPath={setPath} t={t} />
                    )}
                    {tab === "cta" && (
                        <CtaTab draft={draft} setPath={setPath} t={t} />
                    )}
                </div>

                <div className="mt-6 flex justify-end">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="settings-save-bottom"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-navy hover:bg-brand-navy-soft disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Save className="w-4 h-4" /> {saving ? "…" : t("settings.save_all")}
                    </button>
                </div>
            </div>
        </AdminLayout>
    );
}

// ------- Tabs -------

function IdentityTab({ draft, setPath, t }) {
    const idn = draft.identity || {};
    return (
        <div className="space-y-8">
            <div className="bg-brand-paper border border-gray-100 p-4 text-xs text-gray-500" data-testid="identity-note">
                {t("settings.identity_note")}
            </div>
            <Section title={t("settings.identity_logo")} hint={t("settings.identity_logo_hint")}>
                <ImageUpload
                    value={idn.logo_url || ""}
                    onChange={(v) => setPath("identity.logo_url", v)}
                    testid="settings-identity-logo"
                />
            </Section>
            <BilingualRow>
                <Input label={`${t("settings.identity_name")} · ${t("settings.fr")}`} value={idn.name_fr || ""} onChange={(v) => setPath("identity.name_fr", v)} testid="settings-identity-name-fr" />
                <Input label={`${t("settings.identity_name")} · ${t("settings.en")}`} value={idn.name_en || ""} onChange={(v) => setPath("identity.name_en", v)} testid="settings-identity-name-en" />
            </BilingualRow>
            <BilingualRow>
                <Input label={`${t("settings.identity_sub")} · ${t("settings.fr")}`} value={idn.sub_fr || ""} onChange={(v) => setPath("identity.sub_fr", v)} testid="settings-identity-sub-fr" />
                <Input label={`${t("settings.identity_sub")} · ${t("settings.en")}`} value={idn.sub_en || ""} onChange={(v) => setPath("identity.sub_en", v)} testid="settings-identity-sub-en" />
            </BilingualRow>
        </div>
    );
}

function ContactTab({ draft, setPath, t }) {
    return (
        <div className="space-y-8">
            <Section title={t("settings.phones")} hint={t("settings.phones_hint")}>
                {(draft.contact?.phones || []).map((_, i) => (
                    <div key={i} className="grid sm:grid-cols-2 gap-4 mb-3">
                        <Input label={`${t("settings.phone_display")} #${i + 1}`} value={draft.contact.phones[i]?.display || ""} onChange={(v) => setPath(`contact.phones.${i}.display`, v)} testid={`settings-phone-display-${i}`} />
                        <Input label={`${t("settings.phone_wa")} #${i + 1}`} value={draft.contact.phones[i]?.waNumber || ""} onChange={(v) => setPath(`contact.phones.${i}.waNumber`, v)} testid={`settings-phone-wa-${i}`} />
                    </div>
                ))}
            </Section>
            <Section title={t("settings.emails")}>
                {(draft.contact?.emails || []).map((_, i) => (
                    <Input key={i} label={`Email ${i + 1}`} value={draft.contact.emails[i] || ""} onChange={(v) => setPath(`contact.emails.${i}`, v)} testid={`settings-email-${i}`} />
                ))}
            </Section>
            <div className="grid sm:grid-cols-2 gap-4">
                <Input label={t("settings.website")} value={draft.contact?.website || ""} onChange={(v) => setPath("contact.website", v)} testid="settings-website" />
                <Input label={t("settings.address")} value={draft.contact?.address || ""} onChange={(v) => setPath("contact.address", v)} testid="settings-address" />
            </div>
        </div>
    );
}

function HoursTab({ draft, setPath, t }) {
    return (
        <div className="grid sm:grid-cols-2 gap-5">
            <Input label={t("settings.mon_thu")} value={draft.hours?.mon_thu || ""} onChange={(v) => setPath("hours.mon_thu", v)} testid="settings-hours-mon-thu" />
            <Input label={t("settings.fri")} value={draft.hours?.fri || ""} onChange={(v) => setPath("hours.fri", v)} testid="settings-hours-fri" />
            <Input label={t("settings.sat")} value={draft.hours?.sat || ""} onChange={(v) => setPath("hours.sat", v)} testid="settings-hours-sat" />
            <Input label={t("settings.sun")} value={draft.hours?.sun || ""} onChange={(v) => setPath("hours.sun", v)} testid="settings-hours-sun" />
        </div>
    );
}

function SocialsTab({ draft, setPath, t }) {
    return (
        <div className="grid sm:grid-cols-2 gap-5">
            <Input label={t("settings.facebook_url")} value={draft.socials?.facebook || ""} onChange={(v) => setPath("socials.facebook", v)} testid="settings-facebook" />
            <Input label={t("settings.instagram_url")} value={draft.socials?.instagram || ""} onChange={(v) => setPath("socials.instagram", v)} testid="settings-instagram" />
            <Input label={t("settings.whatsapp_default")} value={draft.socials?.whatsapp_default || ""} onChange={(v) => setPath("socials.whatsapp_default", v)} testid="settings-wa-default" />
        </div>
    );
}

function BrandTab({ draft, setPath, t }) {
    return (
        <div className="max-w-md">
            <Input label={t("settings.clients_satisfied")} type="number" value={draft.clients_satisfied || 0} onChange={(v) => setPath("clients_satisfied", v)} testid="settings-clients-satisfied" />
        </div>
    );
}

function HeroTab({ draft, setPath, t }) {
    return (
        <div className="space-y-5">
            <Input label={t("settings.hero_image")} value={draft.hero?.image_url || ""} onChange={(v) => setPath("hero.image_url", v)} testid="settings-hero-image" />
            <BilingualRow>
                <Input label={`${t("settings.hero_kicker")} · ${t("settings.fr")}`} value={draft.hero?.kicker_fr || ""} onChange={(v) => setPath("hero.kicker_fr", v)} testid="settings-hero-kicker-fr" />
                <Input label={`${t("settings.hero_kicker")} · ${t("settings.en")}`} value={draft.hero?.kicker_en || ""} onChange={(v) => setPath("hero.kicker_en", v)} testid="settings-hero-kicker-en" />
            </BilingualRow>
            <BilingualRow>
                <Input label={`${t("settings.hero_title_a")} · ${t("settings.fr")}`} value={draft.hero?.title_a_fr || ""} onChange={(v) => setPath("hero.title_a_fr", v)} testid="settings-hero-title-a-fr" />
                <Input label={`${t("settings.hero_title_a")} · ${t("settings.en")}`} value={draft.hero?.title_a_en || ""} onChange={(v) => setPath("hero.title_a_en", v)} testid="settings-hero-title-a-en" />
            </BilingualRow>
            <BilingualRow>
                <Input label={`${t("settings.hero_title_b")} · ${t("settings.fr")}`} value={draft.hero?.title_b_fr || ""} onChange={(v) => setPath("hero.title_b_fr", v)} testid="settings-hero-title-b-fr" />
                <Input label={`${t("settings.hero_title_b")} · ${t("settings.en")}`} value={draft.hero?.title_b_en || ""} onChange={(v) => setPath("hero.title_b_en", v)} testid="settings-hero-title-b-en" />
            </BilingualRow>
            <BilingualRow>
                <TextArea label={`${t("settings.hero_desc")} · ${t("settings.fr")}`} value={draft.hero?.description_fr || ""} onChange={(v) => setPath("hero.description_fr", v)} testid="settings-hero-desc-fr" />
                <TextArea label={`${t("settings.hero_desc")} · ${t("settings.en")}`} value={draft.hero?.description_en || ""} onChange={(v) => setPath("hero.description_en", v)} testid="settings-hero-desc-en" />
            </BilingualRow>
        </div>
    );
}

function ServicesTab({ draft, setPath, t }) {
    return (
        <div className="space-y-8">
            {(draft.services || []).map((s, i) => (
                <div key={i} className="border border-gray-100 p-5" data-testid={`settings-service-${i}`}>
                    <div className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-gold mb-3">Service #{i + 1}</div>
                    <div className="grid sm:grid-cols-2 gap-4 mb-3">
                        <Input label={t("settings.service_key")} value={s.key || ""} onChange={(v) => setPath(`services.${i}.key`, v)} testid={`settings-svc-key-${i}`} />
                        <Input label={t("settings.service_icon")} value={s.icon || ""} onChange={(v) => setPath(`services.${i}.icon`, v)} testid={`settings-svc-icon-${i}`} />
                    </div>
                    <BilingualRow>
                        <Input label={`${t("settings.service_title")} · ${t("settings.fr")}`} value={s.title_fr || ""} onChange={(v) => setPath(`services.${i}.title_fr`, v)} testid={`settings-svc-title-fr-${i}`} />
                        <Input label={`${t("settings.service_title")} · ${t("settings.en")}`} value={s.title_en || ""} onChange={(v) => setPath(`services.${i}.title_en`, v)} testid={`settings-svc-title-en-${i}`} />
                    </BilingualRow>
                    <BilingualRow>
                        <TextArea label={`${t("settings.service_text")} · ${t("settings.fr")}`} value={s.text_fr || ""} onChange={(v) => setPath(`services.${i}.text_fr`, v)} testid={`settings-svc-text-fr-${i}`} />
                        <TextArea label={`${t("settings.service_text")} · ${t("settings.en")}`} value={s.text_en || ""} onChange={(v) => setPath(`services.${i}.text_en`, v)} testid={`settings-svc-text-en-${i}`} />
                    </BilingualRow>
                </div>
            ))}
        </div>
    );
}

function AboutTab({ draft, setPath, t }) {
    const valuesFr = (draft.about?.values_fr || []).join("\n");
    const valuesEn = (draft.about?.values_en || []).join("\n");
    return (
        <div className="space-y-5">
            <Input label={t("settings.about_image")} value={draft.about?.image_url || ""} onChange={(v) => setPath("about.image_url", v)} testid="settings-about-image" />
            <BilingualRow>
                <Input label={`${t("settings.about_kicker")} · ${t("settings.fr")}`} value={draft.about?.kicker_fr || ""} onChange={(v) => setPath("about.kicker_fr", v)} testid="settings-about-kicker-fr" />
                <Input label={`${t("settings.about_kicker")} · ${t("settings.en")}`} value={draft.about?.kicker_en || ""} onChange={(v) => setPath("about.kicker_en", v)} testid="settings-about-kicker-en" />
            </BilingualRow>
            <BilingualRow>
                <Input label={`${t("settings.about_title")} · ${t("settings.fr")}`} value={draft.about?.title_fr || ""} onChange={(v) => setPath("about.title_fr", v)} testid="settings-about-title-fr" />
                <Input label={`${t("settings.about_title")} · ${t("settings.en")}`} value={draft.about?.title_en || ""} onChange={(v) => setPath("about.title_en", v)} testid="settings-about-title-en" />
            </BilingualRow>
            <BilingualRow>
                <TextArea label={`${t("settings.about_text_1")} · ${t("settings.fr")}`} value={draft.about?.text_1_fr || ""} onChange={(v) => setPath("about.text_1_fr", v)} testid="settings-about-text1-fr" />
                <TextArea label={`${t("settings.about_text_1")} · ${t("settings.en")}`} value={draft.about?.text_1_en || ""} onChange={(v) => setPath("about.text_1_en", v)} testid="settings-about-text1-en" />
            </BilingualRow>
            <BilingualRow>
                <TextArea label={`${t("settings.about_text_2")} · ${t("settings.fr")}`} value={draft.about?.text_2_fr || ""} onChange={(v) => setPath("about.text_2_fr", v)} testid="settings-about-text2-fr" />
                <TextArea label={`${t("settings.about_text_2")} · ${t("settings.en")}`} value={draft.about?.text_2_en || ""} onChange={(v) => setPath("about.text_2_en", v)} testid="settings-about-text2-en" />
            </BilingualRow>
            <BilingualRow>
                <Input label={`${t("settings.about_values_title")} · ${t("settings.fr")}`} value={draft.about?.values_title_fr || ""} onChange={(v) => setPath("about.values_title_fr", v)} testid="settings-about-vtitle-fr" />
                <Input label={`${t("settings.about_values_title")} · ${t("settings.en")}`} value={draft.about?.values_title_en || ""} onChange={(v) => setPath("about.values_title_en", v)} testid="settings-about-vtitle-en" />
            </BilingualRow>
            <BilingualRow>
                <TextArea label={`${t("settings.about_values")} · ${t("settings.fr")}`} value={valuesFr} onChange={(v) => setPath("about.values_fr", v.split("\n").filter((x) => x.trim() !== ""))} testid="settings-about-values-fr" rows={6} />
                <TextArea label={`${t("settings.about_values")} · ${t("settings.en")}`} value={valuesEn} onChange={(v) => setPath("about.values_en", v.split("\n").filter((x) => x.trim() !== ""))} testid="settings-about-values-en" rows={6} />
            </BilingualRow>
        </div>
    );
}

function CtaTab({ draft, setPath, t }) {
    return (
        <div className="space-y-5">
            <BilingualRow>
                <Input label={`${t("settings.cta_text")} · ${t("settings.fr")}`} value={draft.cta_band?.text_fr || ""} onChange={(v) => setPath("cta_band.text_fr", v)} testid="settings-cta-text-fr" />
                <Input label={`${t("settings.cta_text")} · ${t("settings.en")}`} value={draft.cta_band?.text_en || ""} onChange={(v) => setPath("cta_band.text_en", v)} testid="settings-cta-text-en" />
            </BilingualRow>
            <BilingualRow>
                <Input label={`${t("settings.cta_button")} · ${t("settings.fr")}`} value={draft.cta_band?.button_fr || ""} onChange={(v) => setPath("cta_band.button_fr", v)} testid="settings-cta-btn-fr" />
                <Input label={`${t("settings.cta_button")} · ${t("settings.en")}`} value={draft.cta_band?.button_en || ""} onChange={(v) => setPath("cta_band.button_en", v)} testid="settings-cta-btn-en" />
            </BilingualRow>
        </div>
    );
}

// ------- primitives -------
function Section({ title, hint, children }) {
    return (
        <section>
            <div className="text-[0.65rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-2">{title}</div>
            {hint ? <p className="text-xs text-gray-500 mb-3">{hint}</p> : null}
            {children}
        </section>
    );
}

function BilingualRow({ children }) {
    return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function Input({ label, value, onChange, type = "text", testid }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full mt-1.5 bg-white border border-gray-200 px-4 py-2.5 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors"
            />
        </label>
    );
}

function TextArea({ label, value, onChange, testid, rows = 3 }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{label}</span>
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                data-testid={testid}
                className="w-full mt-1.5 bg-white border border-gray-200 px-4 py-2.5 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors resize-y"
            />
        </label>
    );
}
